<script setup>
import { ref, onMounted} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SaveBtn from '@/components/layout/SaveBtn.vue'
import BaseForm from '@/components/autoform/BaseForm.vue'
import NavBarActions from '@/components/layout/NavBarActions'
import { VehicleService } from '@/services/vehicle-service'
import { InspectionService } from '@/services/inspection-service'
import { useVehicleStore, storeReady as vStoreReady } from '@/stores/vehicle-store'
import { useInspectionStore, storeReady as iStoreReady } from '@/stores/inspection-store'
import { Log } from '@/modules/util/log'
import { Settings } from '@/settings'
import { traverse } from '@/modules/util/object'
import { NAMA } from '@/modules/nama/main'
import { HTTP } from '@/services/http'
import { Notify } from '@/modules/util/notify'

const vehicleService = new VehicleService
const inspectionService = new InspectionService
const route = useRoute()
const router = useRouter()
const vehicleStore = useVehicleStore()
const inspectionStore = useInspectionStore()
const { INSPECTION_TYPES } = Settings

const loading = ref(true)
const submitting = ref(false)
const config = ref(null)
const data = ref({})
const form = ref(null)
const fetchers = ref([])

const getVehicle = async () => {
	const promise = vehicleService.get(route.params.id).then(res => {
		if (!(res.body?.status && res.body?.vehicle)) {
			throw new Error('Failed to fetch data')
		}

		const { vehicle } = res.body

		if (
			vehicleStore.currentVehicle &&
			vehicleStore.currentVehicle?.id !== vehicle.id
		) {
			return
		}

		data.value = {
			...data.value,
			vehicle: {
				...vehicle,
				...data.value.vehicle,
			},
		}

		vehicleStore.setCurrentVehicle(data.value.vehicle)
		return vehicle
	}).catch(e => {
		Log.warning(
			'Could not fetch latest vehicle data. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.currentVehicle || await promise    
	data.value = {
		...data.value,
		vehicle: {
			...(item || {}),
			...data.value.vehicle,
		}
	}

	return item 
}

const listInspectors = async () => {
	const promise = inspectionService.inspectors().then(res => {
		if (!(res.body?.status && res.body?.inspectors)) {
			throw new Error('Failed to fetch data')
		}

		inspectionStore.setInspectors(res.body.inspectors)
		return res.body.inspectors
	}).catch(e => {
		Log.warning(
			'Could not refresh inspector list. You may have poor connectivity.',
			e.message || e,
		)
	})

	await iStoreReady

	return inspectionStore.allInspectors || await promise
}

const listTyreManufacturers = async () => {
	const promise = vehicleService.tyreManufacturers().then(res => {
		if (!(res.body?.status && res.body?.manufacturers)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setTyreManufacturers(res.body.manufacturers)
		return res.body.manufacturers
	}).catch(e => {
		Log.warning(
			'Could not refresh tyre manufacturers. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	return vehicleStore.allTyreManufacturers || await promise
}

const listBodyBuilders = async () => {
	const promise = vehicleService.bodyBuilders().then(res => {
		if (!(res.body?.status && res.body?.bodybuilders)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setBodyBuilders(res.body.bodybuilders)
		return res.body.bodybuilders
	}).catch(e => {
		Log.warning(
			'Could not refresh body builders. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.allBodyBuilders || await promise
	item.push({ name: '__other', title: 'Other...' })

	return item
}

const listBodyStyles = async () => {
	const promise = vehicleService.bodyStyles().then(res => {
		if (!(res.body?.status && res.body?.bodystyles)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setBodyStyles(res.body.bodystyles)
		return res.body.bodystyles
	}).catch(e => {
		Log.warning(
			'Could not refresh body builders. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.allBodyStyles || await promise
	item.push({ name: '__other', title: 'Other...' })

	return item
}

const listCabTypes = async () => {
	const promise = vehicleService.cabTypes().then(res => {
		if (!(res.body?.status && res.body?.cabtypes)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setCabTypes(res.body.cabtypes)
		return res.body.cabtypes
	}).catch(e => {
		Log.warning(
			'Could not refresh body builders. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.allCabTypes || await promise
	item.push({ name: '__other', title: 'Other...' })

	return item
}

const listFeatures = async () => {
	const promise = vehicleService.features(route.params.id).then(res => {
		if (!(res.body?.status && res.body?.features)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setFeatures(res.body.features)
		
		return res.body.features
	}).catch(e => {
		Log.warning(
			'Could not refresh inspection features. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	return vehicleStore.allFeatures || await promise
}

const listGrades = async () => {
	const promise = vehicleService.grades().then(res => {		
		if (!(res.body?.status && res.body?.grades)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setGrades(res.body.grades)
		return res.body.grades
	}).catch(e => {
		Log.warning(
			'Could not refresh vehicle grades. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	return vehicleStore.allGrades || await promise
}

const listNamaGrades = async () => {
	const grades = (await listGrades())['nama']

	if (grades.find(val => val.motorvehiclegrade_value === 'AUTO')) {
		return grades
	}
    
	grades.unshift({ 
		motorvehiclegrade_label: 'Auto', 
		motorvehiclegrade_value: 'AUTO' 
	}, {
		motorvehiclegrade_label: 'Do not grade', 
		motorvehiclegrade_value: 'NOGRADE' 
	})

	return grades
}

const listExteriorGrades = async () => {
	return (await listGrades())['exterior']
}

const listInteriorGrades = async () => {
	return (await listGrades())['interior']
}

const initNAMA = async () => {
	const lcv = data.value.vehicle?.classification_id == '2' ||
		route.params.typeId == '10'
            
	await iStoreReady
    
	const promise = inspectionService.namaGradeValues().then(res => {
		if (!res.body?.status) {
			throw new Error(JSON.stringify(res.body))
		}

		const { gradevalues, thresholds } = res.body

		inspectionStore.setNamaGradeValues(gradevalues)
		inspectionStore.setNamaGradeThresholds(thresholds)

		NAMA.init(
			gradevalues, 
			thresholds, 
			inspectionStore.currentDamageItems,
			lcv,
		)
	}).catch(e => {
		console.warn('initNAMA:', e.message || e)
	})

	let gradevalues = inspectionStore.allNamaGradeValues
	let thresholds = inspectionStore.allNamaGradeThresholds
    
	if (gradevalues && thresholds) {
		const lcv = data.value.vehicle?.classification_id == '2' ||
			route.params.typeId == '10'
            
		NAMA.init(
			gradevalues, 
			thresholds, 
			inspectionStore.currentDamageItems,
			lcv,
		)
            
		inspectionStore.setNamaGrade(NAMA.grade())
		return
	}

	await promise

	gradevalues = inspectionStore.allNamaGradeValues
	if (!gradevalues) {
		Notify.error(
			'Failed to load NAMA grade data from network or cache.' +
				'Please initialize this inspection type with a network connection.',
		)
		router.push({ name: 'get-vehicle', params: { id: route.params.id } })
	}
}

const saveInspection = async () => {
	await form.value.validate()

	if (!form.value.valid || submitting.value) {
		return
	}

	submitting.value = true

	try {
		if (data.value.inspection) {
			vehicleStore.tyreTypes.forEach(type => {
				if (!(data.value.inspection[type] && data.value.inspection[type].treaddepth)) {
					return
				}

				const treadParts = data.value.inspection[type].treaddepth.split('.')
				data.value.inspection[type].treaddepthinteger = treadParts[0]
				data.value.inspection[type].treaddepthfractional = treadParts[1]
				
				delete data.value.inspection[type].treaddepth
			})

			data.value.inspection.splat = inspectionStore.currentNamaSplat
		}

		const res = await inspectionService.save(
			route.params.id, 
			route.params.typeId,
			data.value,
		)

		if (res.uid) {
			return
		}

		throw new Error(JSON.stringify(res))
	} catch (e) {
		Notify.error('Failed to save inspection')
		console.error('saveInspection:', e.message || e, data.value.vehicle ? data.value.vehicle.registration : '')
	} finally {
		Settings.clearProgress(true)

		inspectionStore.setNamaSplat(null)
		inspectionStore.setCurrentInspection(null)
		inspectionStore.setDamageItems([])

		router.push({ name: 'get-vehicle', params: { id: route.params.id } })
	}
}

const getRequestedInspection = async () => {
	let inspection = null

	if (route.params.inspectionId) {
		await iStoreReady
		inspection = (inspectionStore.allInspections || [])
			.find(val => val.motorvehicleinspection_id == route.params.inspectionId)
			?.motorvehicleinspection_data
	}

	return inspection
}

const getInProgressInspection = async () => {
	let inspection = null

	const cached = Settings.getProgress() || {}

	if (
		cached.vehicleId == route.params.id && 
		cached.inspectionId == route.params.typeId
	) {
		// restore progress
		inspection = cached.data.inspection
		if (cached.data.vehicle) {
			data.value.vehicle = cached.data.vehicle
		}
	} else {
		// clear up cached images we no longer need
		await traverse(cached?.data || {}, (key, value) => {
			if (!value?.uid) {
				return
			}

			HTTP.deleteCached(value.uid)
		})
	}

	return inspection
}

const getInspection = async () => {
	let inspection = await getRequestedInspection() || await getInProgressInspection()

	if (inspection) {
		vehicleStore.tyreTypes.forEach(type => {
			if (!(
				inspection[type] && 
				(inspection[type].treaddepthinteger || inspection[type].treaddepthfractional) &&
				!inspection[type].treaddepth
			)) {
				return
			}

			let int = inspection[type].treaddepthinteger
			let frac = inspection[type].treaddepthfractional

			int = int ? Number(int) : 0
			frac = frac ? Number(frac) : 0

			inspection[type].treaddepth = `${int}.${frac}`
		})

		data.value.inspection = inspection
	} else {
		inspection = {}
	}

	inspectionStore.setCurrentInspection(inspection)
	inspectionStore.setDamageItems(inspection?.damageitems || [])
	inspectionStore.setNamaSplat(inspection.data?.inspection?.splat || null)
}

const setInspectionType = async () => {
	const type = INSPECTION_TYPES.filter(val => val.id == route.params.typeId)[0]
	config.value = type.data.config

	const fMap = {
		getVehicle,
		listInspectors,
		listTyreManufacturers,
		listBodyBuilders,
		listBodyStyles,
		listCabTypes,
		listFeatures,
		listNamaGrades,
		listExteriorGrades,
		listInteriorGrades,
	}

	const fNames = []
	await traverse(config.value, (key, value) => {
		if (key === 'fetcher') {
			fNames.push(value)
			return false
		}

		return true
	})
	
	fetchers.value = [
		getVehicle,
		...fNames.map(val => {
			if (!fMap[val]) {
				throw new Error(`Unknown fetcher specified in config: ${val}`)	
			}

			return fMap[val]
		}),
	]
}

const cacheProgress = () => {
	if (inspectionStore.currentNamaSplat) {
		data.value.inspection.splat = inspectionStore.currentNamaSplat
	}

	Settings.cacheProgress(route.params.id, route.params.typeId, data.value)
}

const onReady = async () => {
	HTTP.initCacheRetry()
	inspectionStore.currentDamageItems.forEach(NAMA.add)
}

onMounted(async () => {
	await setInspectionType()
	await getInspection()
	await initNAMA()

	loading.value = false
})

</script>

<template>
<BaseForm
	v-if="!loading"
	ref="form"
	v-model="data"
	:config="config"
	:fetchers="fetchers"
	@ready="onReady"
	@submit="saveInspection"
	@update="cacheProgress"
/>

<NavBarActions>
	<div class="d-flex flex-row">
		<v-btn
			v-show="data.inspection && data.inspection.namagrade === 'AUTO'"
			variant="tonal"
			class="mr-3 nama"
		>
			<span>NAMA</span> {{ inspectionStore.currentNamaGrade }}
		</v-btn>
		<SaveBtn
			:loading="submitting"
			:base-form="form"
		/>
	</div>
</NavBarActions>
</template>

<style lang="scss" scoped>
.nama {
	pointer-events: none;
	font-weight: 900;

	span {
		letter-spacing: .25rem;
		font-weight: normal;
		margin-right: .25rem;
	}
}
</style>